import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  EyeOff, 
  Loader,
  Lock,
  Unlock,
  Send,
  Bell,
  FileText,
  Download,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Shield,
  Search,
  Users
} from 'lucide-react';
import toast from 'react-hot-toast';
import resultApi from '../../api/services/resultApi';
import EXAM_API from '../../api/services/examApi';
import sectionApi from '../../api/services/sectionApi';
import { useData } from '../../contexts/DataContext';
import { API_CONFIG } from '../../api/config';
import RecipientSelector from '../../components/Common/RecipientSelector';

const PublishResults = () => {
  const { classes: apiClasses, students, whatsappConfigured } = useData();
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [classStatuses, setClassStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [expandedClass, setExpandedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [sendingToStudent, setSendingToStudent] = useState(null);
  const [targetAudience, setTargetAudience] = useState(() => {
    const orgType = localStorage.getItem('organizationType')?.toUpperCase();
    return orgType === 'COLLEGE' ? 'both' : 'parent';
  });
  
  // Section States
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [loadingSections, setLoadingSections] = useState(false);

  useEffect(() => {
    loadExams();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchSections(selectedClass);
    } else {
      setSections([]);
      setSelectedSection('');
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedExam && selectedClass) {
      loadClassStatuses();
    }
  }, [selectedExam, selectedSection, selectedClass]);

  const loadExams = async () => {
    try {
      const response = await EXAM_API.getAllExams();
      const examsList = response.data?.exams || response.data || [];
      const normalizedExams = examsList.map(exam => ({
        ...exam,
        id: exam._id || exam.id || exam.examId
      }));
      setExams(normalizedExams);
    } catch (error) {
      toast.error('Failed to load exams');
    }
  };

  const loadClassStatuses = async () => {
    if (!selectedExam || !selectedClass) return;
    setLoadingStatuses(true);
    try {
      // 1. Fetch Exam Details to see assignments
      const examResp = await EXAM_API.getExamById(selectedExam);
      const exam = examResp.data;
      const assignedUnits = exam?.classes || [];
      const assignedIds = new Set(assignedUnits.map(u => u.unitId?._id || u.unitId || u.id));

      // 2. Fetch all results for this exam AND class
      const response = await resultApi.getAllResults({
        examId: selectedExam,
        unitId: selectedClass,
        ...(selectedSection && { sectionId: selectedSection })
      });
      const allResults = response.data?.results || response.data || [];

      // 3. Construct class map for the SELECTED class only
      const classMap = {};
      const clsInfo = apiClasses.find(c => c._id === selectedClass);
      
      if (clsInfo) {
        classMap[selectedClass] = {
            classId: selectedClass,
            className: clsInfo.unitName,
            studentStatus: {}
        };
      }

      // Process results
      allResults.forEach(r => {
        const cid = r.unitId?._id || r.unitId;
        const sid = r.studentRegistrationId;
        
        if (!cid || !sid) return;
        if (cid !== selectedClass) return; // Should not happen due to API filter, but safety check

        if (classMap[cid]) {
            if (classMap[cid].studentStatus[sid] === undefined) {
              classMap[cid].studentStatus[sid] = true;
            }
            if (r.publishStatus !== 'published') {
              classMap[cid].studentStatus[sid] = false;
            }
        }
      });

      // 4. Convert to status array
      const statuses = Object.values(classMap).map(cls => {
        const studentValues = Object.values(cls.studentStatus);
        const totalCount = studentValues.length;
        const publishedCount = studentValues.filter(v => v).length;
        
        let status = 'no-results';
        if (totalCount > 0) {
          status = publishedCount === totalCount ? 'published' : publishedCount === 0 ? 'draft' : 'partial';
        }

        return {
          classId: cls.classId,
          className: cls.className,
          totalStudents: totalCount,
          publishedCount,
          status
        };
      });

      setClassStatuses(statuses);
    } catch (error) {
      toast.error('Failed to sync publish status');
    } finally {
      setLoadingStatuses(false);
    }
  };

  const fetchSections = async (unitId) => {
    setLoadingSections(true);
    try {
      const response = await sectionApi.getSectionsByUnit(unitId);
      if (response.success) {
        setSections(response.data || []);
      }
    } catch (error) {
      // Silently ignore section loading errors
    } finally {
      setLoadingSections(false);
    }
  };

  const loadClassStudents = async (classId) => {
    try {
      const response = await resultApi.getAllResults({
        examId: selectedExam,
        unitId: classId,
        ...(selectedSection && { sectionId: selectedSection }),
        publishStatus: 'published'
      });

      const results = response.data?.results || response.data || [];
      const studentMap = {};
      results.forEach(result => {
        const regId = result.studentRegistrationId;
        if (!studentMap[regId]) {
          studentMap[regId] = {
            registrationId: regId,
            name: result.studentName || `${result.studentId?.firstName} ${result.studentId?.lastName}`,
            phoneNumber: result.studentId?.phoneNumber || students.find(s => s.studentRegistrationId === regId)?.phoneNumber,
            results: []
          };
        }
        studentMap[regId].results.push(result);
      });
      
      setClassStudents(Object.values(studentMap));
    } catch (error) {
      toast.error('Failed to load students');
    }
  };

  const handleExpandClass = async (classId) => {
    if (expandedClass === classId) {
      setExpandedClass(null);
      setClassStudents([]);
    } else {
      setExpandedClass(classId);
      await loadClassStudents(classId);
    }
  };

  const handlePublishClass = async (classId, action) => {
    setPublishing(true);
    try {
      await resultApi.publishResults(selectedExam, action, classId, selectedSection || null);
      toast.success(`✅ Results ${action === 'published' ? 'published' : 'unpublished'}!`);
      loadClassStatuses();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setPublishing(false);
    }
  };

  const handleBulkPublish = async (action) => {
    const targetClasses = classStatuses.filter(cs => cs.totalStudents > 0 && cs.status !== action);
    if (targetClasses.length === 0) {
      toast.error('No classes to update');
      return;
    }

    setPublishing(true);
    let successCount = 0;
    for (const cls of targetClasses) {
      try {
        await resultApi.publishResults(selectedExam, action, cls.classId, selectedSection || null);
        successCount++;
      } catch (error) {
        // Continue with next class on error
      }
    }

    toast.success(`✅ Updated ${successCount} classes!`);
    setPublishing(false);
    loadClassStatuses();
  };

  const handleSendToStudent = async (student) => {
    setSendingToStudent(student.registrationId);
    try {
      const payload = {
        examId: selectedExam,
        unitId: expandedClass,
        sectionId: selectedSection || null,
        studentRegistrationId: student.registrationId,
        targetAudience
      };
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/result-notifications/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Notification sent to ${student.name}!`);
      } else {
        toast.error(data.message || 'Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSendingToStudent(null);
    }
  };

  const handleSendToClass = async (classId) => {
    setSendingNotifications(true);
    try {
      const payload = {
        examId: selectedExam,
        unitId: classId,
        sectionId: selectedSection || null,
        targetAudience
      };
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/result-notifications/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Sent: ${data.data?.sent || 0}, Failed: ${data.data?.failed || 0}`);
      } else {
        toast.error(data.message || 'Failed to send');
      }
    } catch (error) {
      toast.error('Failed to send notifications');
    } finally {
      setSendingNotifications(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header & Configuration */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
            <h2 className="text-lg font-bold text-slate-900">Publish Results</h2>
            <p className="text-xs text-slate-500">Manage result visibility and notify students/parents via WhatsApp</p>
        </div>

        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                
                {/* Step 1: Class Selection */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                        <span 
                          className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-bold"
                          style={{ backgroundColor: 'var(--primary-color)' }}
                        >1</span>
                        Select Class
                    </label>
                    <div className="relative">
                        <select
                            value={selectedClass}
                            onChange={(e) => {
                                setSelectedClass(e.target.value);
                                setSelectedSection(''); // Reset section when class changes
                            }}
                            className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-[var(--primary-color)] focus:ring-1 focus:ring-[var(--primary-color)] outline-none appearance-none cursor-pointer"
                        >
                            <option value="">Choose Class...</option>
                            {apiClasses.map(cls => (
                                <option key={cls._id} value={cls._id}>{cls.unitName}</option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Step 2: Section Selection (Optional but sequential) */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                        <span 
                          className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-bold"
                          style={{ backgroundColor: 'var(--primary-color)' }}
                        >2</span>
                        Select Section
                    </label>
                    <div className="relative">
                        <select
                            value={selectedSection}
                            onChange={(e) => setSelectedSection(e.target.value)}
                            disabled={!selectedClass || loadingSections}
                            className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-[var(--primary-color)] focus:ring-1 focus:ring-[var(--primary-color)] outline-none appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                            <option value="">All Sections</option>
                            {sections.map(section => (
                                <option key={section._id} value={section._id}>{section.name || section.sectionName}</option>
                            ))}
                        </select>
                         {loadingSections ? (
                            <Loader className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-[var(--primary-color)]" />
                        ) : (
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        )}
                    </div>
                </div>

                {/* Step 3: Exam Selection */}
                <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                        <span 
                          className="flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-bold"
                          style={{ backgroundColor: 'var(--primary-color)' }}
                        >3</span>
                        Select Exam Series
                    </label>
                    <div className="relative">
                        <select
                            value={selectedExam}
                            onChange={(e) => setSelectedExam(e.target.value)}
                            disabled={!selectedClass}
                            className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-[var(--primary-color)] focus:ring-1 focus:ring-[var(--primary-color)] outline-none appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                        >
                            <option value="">Choose Exam Series...</option>
                            {exams
                                .filter(exam => {
                                    if (!selectedClass) return false;
                                    
                                    // 1. Check if exam is assigned to this class
                                    const classAssignment = exam.classes?.find(cls => {
                                        const classId = cls.unitId?._id || cls.unitId || cls.id;
                                        return classId === selectedClass;
                                    });

                                    if (!classAssignment) return false;

                                    // 2. If a section is selected, check if exam includes this section
                                    // If exam has no specific sections (global to class) or includes this section
                                    if (selectedSection) {
                                        const assignedSections = classAssignment.sectionIds || classAssignment.sections || [];
                                        
                                        // If sections are defined, we must match
                                        if (assignedSections.length > 0) {
                                            const hasSection = assignedSections.some(sec => {
                                                const secId = sec._id || sec;
                                                return secId === selectedSection;
                                            });
                                            if (!hasSection) return false;
                                        }
                                    }

                                    return true;
                                })
                                .map(exam => (
                                <option key={exam.id} value={exam.id}>{exam.examName} ({exam.examType})</option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                </div>

                {/* Optional: Recipient Channel (Only if WhatsApp Configured) */}
                {selectedExam && selectedClass && whatsappConfigured && (
                    <div className="space-y-2 lg:col-span-3 border-t border-gray-100 pt-4 mt-2">
                        <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                             <MessageCircle size={14} className="text-[var(--primary-color)]" />
                            Recipient Channel Configuration
                        </label>
                        <RecipientSelector
                            value={targetAudience}
                            onChange={setTargetAudience}
                            size="sm"
                            className="!p-0 border-none shadow-none"
                        />
                    </div>
                )}
            </div>

            {selectedExam && selectedClass && (
                <div className="flex flex-wrap items-center gap-4 pt-6 border-t border-gray-100">
                    <button
                        onClick={() => handleBulkPublish('published')}
                        disabled={publishing || loadingStatuses}
                        className="px-6 py-2.5 text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-2 shadow-sm disabled:opacity-50 hover:opacity-90"
                        style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                        <Unlock size={14} />
                        Publish Results
                    </button>
                    
                    {whatsappConfigured && (
                        <button
                            onClick={() => handleSendToClass(selectedClass)}
                            disabled={sendingNotifications || publishing || loadingStatuses}
                            className="px-6 py-2.5 text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-2 shadow-sm disabled:opacity-50 hover:opacity-90"
                            style={{ backgroundColor: 'var(--primary-color)' }}
                        >
                            <Send size={14} />
                            Notify All
                        </button>
                    )}

                    <button
                        onClick={() => handleBulkPublish('draft')}
                        disabled={publishing || loadingStatuses}
                        className="px-6 py-2.5 bg-white border border-gray-200 text-slate-700 rounded-xl text-xs font-bold uppercase hover:bg-gray-50 transition-all flex items-center gap-2 shadow-sm disabled:opacity-50"
                    >
                        <Lock size={14} />
                        Unpublish Results
                    </button>
                </div>
            )}
        </div>
      </div>

      {selectedExam && selectedClass && (
        <div className="space-y-5">
          {/* Status List */}
          <div className="grid grid-cols-1 gap-3">
            {loadingStatuses ? (
              <div className="bg-white rounded-xl border border-gray-100 p-20 text-center">
                <Loader className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: 'var(--primary-color)' }} />
                <p className="text-sm font-medium text-slate-500">Synchronizing status matrix...</p>
              </div>
            ) : classStatuses.map((cls) => (
              <div key={cls.classId} className="bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all hover:shadow-md">
                <div 
                  className={`p-4 flex items-center justify-between gap-4 cursor-pointer transition-colors ${expandedClass === cls.classId ? 'bg-slate-50' : 'hover:bg-gray-50/80'}`}
                  onClick={() => handleExpandClass(cls.classId)}
                >
                    <div className="flex items-center gap-4">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all ${expandedClass === cls.classId ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-400'}`}
                             style={expandedClass === cls.classId ? { backgroundColor: 'var(--primary-color)' } : {}}
                        >
                            {expandedClass === cls.classId ? <ChevronDown className="h-5 w-5 rotate-180" /> : <ChevronRight className="h-5 w-5" />}
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900 leading-tight">{cls.className}</h4>
                            <div className="flex items-center gap-2 mt-1">
                                <Users size={12} className="text-slate-400" />
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                                    {cls.publishedCount} / {cls.totalStudents} RESULTS PUBLISHED
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            cls.status === 'published' ? 'bg-green-100 text-green-700' : 
                            cls.status === 'partial' ? 'bg-blue-100 text-blue-700' : 
                            cls.status === 'no-results' ? 'bg-slate-100 text-slate-500' : 'bg-amber-100 text-amber-700'
                        }`}>
                            {cls.status}
                        </span>

                        {cls.status !== 'no-results' && (
                            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                {cls.status !== 'published' ? (
                                    <button
                                        onClick={() => handlePublishClass(cls.classId, 'published')}
                                        disabled={publishing}
                                        className="h-9 px-5 text-white rounded-xl text-xs font-bold uppercase transition-all hover:opacity-90 disabled:opacity-50"
                                        style={{ backgroundColor: 'var(--primary-color)' }}
                                    >
                                        Publish
                                    </button>
                                ) : (
                                    whatsappConfigured && (
                                        <button
                                            onClick={() => handleSendToClass(cls.classId)}
                                            disabled={sendingNotifications}
                                            className="h-9 px-5 text-white rounded-xl text-xs font-bold uppercase transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50 shadow-sm"
                                            style={{ backgroundColor: 'var(--primary-color)' }}
                                        >
                                            <Send size={14} />
                                            Notify All
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Expanded Students List */}
                {expandedClass === cls.classId && (
                    <div className="border-t border-slate-100 p-6 bg-slate-50/50">
                        {classStudents.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {classStudents.map((student) => (
                                    <div key={student.registrationId} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between group hover:border-blue-200 transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center font-bold text-sm text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                                {student.name[0]}
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-slate-900 uppercase leading-none mb-1">{student.name}</p>
                                                <p className="text-[10px] text-slate-500 font-medium">ID: {student.registrationId}</p>
                                            </div>
                                        </div>
                                        {whatsappConfigured && (
                                            <button
                                                onClick={() => handleSendToStudent(student)}
                                                disabled={sendingToStudent === student.registrationId}
                                                className="h-9 w-9 bg-slate-50 text-slate-400 hover:text-white rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                                                title="Send Result via WhatsApp"
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--primary-color)'; e.currentTarget.style.color = 'white'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; e.currentTarget.style.color = ''; }}
                                            >
                                                {sendingToStudent === student.registrationId ? (
                                                    <Loader size={14} className="animate-spin" />
                                                ) : (
                                                    <Send size={14} />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <div className="p-3 bg-white rounded-full inline-block border border-slate-100 mb-2">
                                    <Shield size={20} className="text-slate-300" />
                                </div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No results available to notify</p>
                            </div>
                        )}
                    </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(!selectedExam || !selectedClass) && (
        <div className="bg-gray-50 rounded-xl p-20 text-center border border-gray-200 border-dashed">
            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-sm font-bold text-slate-900">Configure Publication</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">Select a class, section, and examination series to manage results</p>
        </div>
      )}
    </div>
  );
};

export default PublishResults;
